import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import {
  BookingInsurance,
  BookingInsuranceEditForm,
  INSURANCE_TYPE_LABEL,
  InsuranceTraveler,
  downloadInsuranceDocument,
  editInsuranceBooking,
  sanitizeFileName,
} from "src/store/reducers/bookings";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";
import CustomReactSelectWithLabel from "src/layouts/form/CustomReactSelectWithLabel";
import { fetchListInsuranceProviders } from "src/store/reducers/insuranceProviders";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";

type IEditInsuranceBookingCanvas = {
  Title: string;
  editId: string;
  bookingId: string;
  hide: () => void;
  data: BookingInsurance;
};

const EditInsuranceBookingCanvas = ({
  Title,
  editId,
  bookingId,
  hide,
  data,
}: IEditInsuranceBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [downloading, setDownloading] = useState<string>();
  const [travelers, setTravelers] = useState<InsuranceTraveler[]>(
    data?.traveler_info?.length
      ? data.traveler_info
      : [{ name: "", phone: "", date_of_birth: "" }]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const providersList = useAppSelector(
    (state) => state.insuranceProviders.list.data
  );

  useEffect(() => {
    if (!data) {
      hide();
    }
  }, [data, hide]);

  useEffect(() => {
    setTravelers(
      data?.traveler_info?.length
        ? data.traveler_info
        : [{ name: "", phone: "", date_of_birth: "" }]
    );
  }, [editId, data?.traveler_info]);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    setErrors,
    errors,
  } = useFormik<{
    insurance_provider: string;
    policy_number: string;
    insurance_type: number;
    coverage_amount: string;
    premium_amount: string;
    premium_amount_n_currency: string;
    advance_amount_paid: string;
    effective_date: string;
    expiry_date: string;
    policy_terms_conditions: string;
    additional_information: string;
  }>({
    initialValues: {
      insurance_provider: data?.insurance_provider?.id || "",
      policy_number: data?.policy_number || "",
      insurance_type: data?.insurance_type ?? 1,
      coverage_amount: data?.coverage_amount || "",
      premium_amount: data?.premium_amount || "",
      premium_amount_n_currency: data?.premium_amount_n_currency || "",
      advance_amount_paid: data?.advance_amount_paid || "",
      effective_date: data?.effective_date || "",
      expiry_date: data?.expiry_date || "",
      policy_terms_conditions: data?.policy_terms_conditions || "",
      additional_information: data?.additional_information || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      insurance_provider: KeyRequired("Insurance provider"),
      policy_number: KeyRequired("Policy number"),
      premium_amount: Yup.number()
        .required("Premium amount is required")
        .typeError("Premium amount must be a number")
        .positive("Premium amount must be positive"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      editInsurance({
        ...values,
        id: editId,
        traveler_info: travelers,
        booking_id: data.booking_id,
      });
    },
  });

  const editInsurance = useCallback(
    async (values: BookingInsuranceEditForm) => {
      try {
        await dispatch(
          editInsuranceBooking({ form: values, files: newFiles })
        ).unwrap();
        toast.success("Insurance booking is updated successfully");
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, hide, newFiles, setErrors]
  );

  useEffect(() => {
    resetForm();
    setNewFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (editId) {
      dispatch(fetchListInsuranceProviders());
    }
  }, [dispatch, resetForm, editId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const addedFiles = Array.from(e.target.files);
      setNewFiles((prev) => [...prev, ...addedFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownload = useCallback(
    async (documentId: string) => {
      try {
        setDownloading(documentId);
        const url = await dispatch(
          downloadInsuranceDocument({
            booking_id: bookingId,
            insurance_id: data.id,
            document_id: documentId,
          })
        ).unwrap();
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", documentId);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 200);
      } catch (e) {
        toast.error("Failed to download document");
      } finally {
        setDownloading(undefined);
      }
    },
    [bookingId, data, dispatch]
  );

  const addTraveler = () => {
    setTravelers([...travelers, { name: "", phone: "", date_of_birth: "" }]);
  };

  const removeTraveler = (index: number) => {
    setTravelers(travelers.filter((_, i) => i !== index));
  };

  const updateTraveler = (
    index: number,
    field: keyof InsuranceTraveler,
    value: string
  ) => {
    const updated = [...travelers];
    updated[index] = { ...updated[index], [field]: value };
    setTravelers(updated);
  };

  return (
    <Offcanvas
      show={!!editId}
      onHide={hide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button type="button" className="btn-close" onClick={hide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomReactSelectWithLabel
                  labelText="Insurance Provider"
                  placeholder="Insurance Provider"
                  id={"insurance_provider"}
                  required
                  options={providersList.map((p) => ({
                    label: p.provider_name,
                    value: p.id,
                  }))}
                  touched={touched.insurance_provider}
                  errors={errors.insurance_provider}
                  handleChange={handleChange}
                  value={
                    providersList
                      .filter((p) => p.id === values.insurance_provider)
                      ?.map((p) => ({
                        label: p.provider_name,
                        value: p.id,
                      }))?.[0] ||
                    (data?.insurance_provider
                      ? {
                          label: data.insurance_provider.provider_name,
                          value: data.insurance_provider.id,
                        }
                      : "")
                  }
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="policy_number"
                  labelText="Policy Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.policy_number}
                  touched={touched.policy_number}
                  errors={errors.policy_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="insurance_type"
                  labelText="Insurance Type"
                  handleChange={handleChange}
                  value={values.insurance_type}
                  touched={touched.insurance_type}
                  errors={errors.insurance_type}
                >
                  {Object.entries(INSURANCE_TYPE_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="coverage_amount"
                  labelText="Coverage Amount"
                  handleChange={handleChange}
                  value={values.coverage_amount}
                  touched={touched.coverage_amount}
                  errors={errors.coverage_amount}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="premium_amount"
                  labelText="Premium Amount"
                  required={true}
                  handleChange={handleChange}
                  value={values.premium_amount}
                  touched={touched.premium_amount}
                  errors={errors.premium_amount}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="premium_amount_n_currency"
                  labelText="Premium Amount (Local Currency)"
                  handleChange={handleChange}
                  value={values.premium_amount_n_currency}
                  touched={touched.premium_amount_n_currency}
                  errors={errors.premium_amount_n_currency}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="advance_amount_paid"
                  labelText="Advance Amount Paid"
                  handleChange={handleChange}
                  value={values.advance_amount_paid}
                  touched={touched.advance_amount_paid}
                  errors={errors.advance_amount_paid}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="effective_date"
                  labelText="Effective Date"
                  pickerType="datetime"
                  handleChange={(date) => setFieldValue("effective_date", date)}
                  value={values.effective_date}
                  selectsStart
                  endDate={values.expiry_date}
                  touched={touched.effective_date}
                  errors={errors.effective_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="expiry_date"
                  labelText="Expiry Date"
                  pickerType="datetime"
                  handleChange={(date) => setFieldValue("expiry_date", date)}
                  value={values.expiry_date}
                  selectsEnd
                  startDate={values.effective_date}
                  touched={touched.expiry_date}
                  errors={errors.expiry_date}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="policy_terms_conditions"
                  labelText="Policy Terms & Conditions"
                  handleChange={handleChange}
                  value={values.policy_terms_conditions}
                  touched={touched.policy_terms_conditions}
                  errors={errors.policy_terms_conditions}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="additional_information"
                  labelText="Additional Information"
                  handleChange={handleChange}
                  value={values.additional_information}
                  touched={touched.additional_information}
                  errors={errors.additional_information}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Traveler Information</Form.Label>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={addTraveler}
                  >
                    + Add Traveler
                  </button>
                </div>
                {travelers.map((traveler, index) => (
                  <div className="row mb-2 align-items-end" key={index}>
                    <div className="col-xl-4">
                      <CustomInputWithLabel
                        id={`traveler_name_${index}`}
                        placeholder="Name"
                        handleChange={(e: any) =>
                          updateTraveler(index, "name", e.target.value)
                        }
                        value={traveler.name}
                      />
                    </div>
                    <div className="col-xl-3">
                      <CustomInputWithLabel
                        id={`traveler_phone_${index}`}
                        placeholder="Phone"
                        handleChange={(e: any) =>
                          updateTraveler(index, "phone", e.target.value)
                        }
                        value={traveler.phone}
                      />
                    </div>
                    <div className="col-xl-3">
                      <CustomInputWithLabel
                        id={`traveler_dob_${index}`}
                        placeholder="Date of Birth"
                        inputType="date"
                        handleChange={(e: any) =>
                          updateTraveler(index, "date_of_birth", e.target.value)
                        }
                        value={traveler.date_of_birth}
                      />
                    </div>
                    <div className="col-xl-2">
                      {travelers.length > 1 ? (
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeTraveler(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Existing Attachments</Form.Label>
                {data?.attachments &&
                Object.keys(data.attachments).length > 0 ? (
                  <div className="mb-2">
                    {Object.entries(data.attachments).map(([docName]) => (
                      <div
                        key={docName}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">{docName}</span>
                        <button
                          type="button"
                          className="btn btn-primary btn-xs"
                          disabled={downloading === docName}
                          onClick={() => handleDownload(docName)}
                        >
                          {downloading === docName ? (
                            <i className="fa-solid fa-spinner" />
                          ) : (
                            <i className="fa-solid fa-download" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-2">No existing attachments</p>
                )}
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Add New Attachments</Form.Label>
                <Form.Control
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Accepted formats: JPG, JPEG, PNG, PDF (Max 10 files, 5MB each)
                </Form.Text>
                {newFiles.length > 0 && (
                  <div className="mt-2">
                    {newFiles.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">
                          {sanitizeFileName(file.name)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeNewFile(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link to={"#"} onClick={hide} className="btn btn-danger light ms-1">
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default EditInsuranceBookingCanvas;
